import { AxiosError } from 'axios'

import {
  CommentaryData,
  RequestMatchesParams,
  TeamStandingsType,
} from '@sportworld/js-sdk/src/types/soccerDataTypes'
import {
  getCommentaryData,
  reduceMatches,
} from '@sportworld/js-sdk/src/utils/soccerData'

import { commentaryTypes } from '@/constants/common'
import { SoccerDataService } from '@/service'
import useStore from '@/store/useStore'

export const getMatches = async (
  params: RequestMatchesParams,
  abortSignal?: AbortSignal,
) => {
  const { setMatches, setLoading } = useStore.getState().soccerData
  setLoading('matches', true)
  try {
    const data = await SoccerDataService.getMatches(params, abortSignal)

    if (abortSignal?.aborted) {
      return
    }
    const matches = data.reduce(reduceMatches, [])

    setMatches(matches)
  } catch (e) {
    console.error(e)
  } finally {
    setLoading('matches', false)
  }
}

export const getCommentary = async (
  matchId: string,
  setLoading: (val: boolean) => void,
  setCommentaryData: (data: Nullable<CommentaryData>) => void,
  commentaryType?: commentaryTypes,
  abortController?: AbortController,
) => {
  setLoading(true)
  try {
    const res = await SoccerDataService.getCommentary(
      matchId,
      commentaryType,
      abortController?.signal,
    )
    const commentaryData = getCommentaryData(res)
    setCommentaryData(commentaryData)
  } catch (e) {
    console.error(e)
    if (
      e instanceof AxiosError &&
      commentaryType === commentaryTypes.MANUAL &&
      (e.status === 403 || e.status === 404) &&
      !abortController?.signal.aborted
    ) {
      await getCommentary(
        matchId,
        setLoading,
        setCommentaryData,
        commentaryTypes.AUTO,
        abortController,
      )
      return
    }
    setCommentaryData(null)
  } finally {
    !abortController?.signal.aborted && setLoading(false)
  }
}

export const getStandings = async (
  isPopup: boolean,
  calendarId = '',
  abortSignal: AbortSignal,
) => {
  const { setDivision, setDivisionPopup, setLoading, setLoadingPopup } =
    useStore.getState().soccerData

  const setLoadingState = isPopup ? setLoadingPopup : setLoading
  const setDivisionState = isPopup ? setDivisionPopup : setDivision

  if (!calendarId) {
    return setDivisionState([])
  }

  setLoadingState('standings', true)
  try {
    const data = await SoccerDataService.getStandings(calendarId, abortSignal)
    const division = data.division.filter(
      ({ type }) => type === TeamStandingsType.Total,
    )
    setDivisionState(division)
  } catch (e) {
    console.error(e)
  } finally {
    setLoadingState('standings', false)
  }
}

export const getTournamentCalendar = async (calendarId: string) => {
  const { setLoading, setTournamentCalendar } = useStore.getState().soccerData
  setLoading('standings', true)
  try {
    const data = await SoccerDataService.getStandings(calendarId)

    setTournamentCalendar(data.tournamentCalendar)
  } catch (e) {
    console.error(e)
  } finally {
    setLoading('standings', false)
  }
}
